import { PARTY_ROLE, PARTY_ROLE_DISPLAY, type PartyRoleId } from '@steerfi/shared';
import { IconPlus } from '@tabler/icons-react';
import type React from 'react';
import { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import type { ProviderApiRow, ProviderListQuery, ProviderStatusFilter } from '@/api/providers';
import { AddProviderModal } from '@/components/modals/add-provider-modal';
import { DeleteProviderDialog } from '@/components/modals/delete-provider-dialog';
import { EditProviderModal } from '@/components/modals/edit-provider-modal';
import { ProvidersTable } from '@/components/providers-table';
import { SearchInput } from '@/components/search-input';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { MultiSelect } from '@/components/ui/multi-select';
import { useProviders } from '@/hooks/use-providers';

/**
 * Every party role, labelled from the shared constants and derived from
 * `PARTY_ROLE` rather than listed — the same thing `add-provider-modal.tsx` does.
 *
 * All nine, not the four the ticket's acceptance criteria name: Add Provider lets
 * a provider be created with any role, so a narrower filter cannot reach rows the
 * app itself produces. Deriving keeps this list and that form in step.
 */
const ROLE_OPTIONS: { value: string; label: string }[] = Object.values(PARTY_ROLE).map((id) => ({
  value: String(id),
  label: PARTY_ROLE_DISPLAY[id as PartyRoleId],
}));

const STATUS_OPTIONS: { value: string; label: string }[] = [
  { value: 'active', label: 'Active' },
  { value: 'inactive', label: 'Inactive' },
];

/**
 * Sort fields the API accepts. Mirrors the `sortBy` enum in `queryProviderSchema`
 * (`services/api-server/src/dtos/provider.dto.ts`), which answers anything else
 * with a 400 — so an unrecognised value from the URL is dropped here rather than
 * forwarded, and the page loads on the default sort instead of failing.
 *
 * Deliberately **not** `SORTABLE_COLS` from `providers-table.tsx`, which lists only
 * the three columns rendered from a loop. Status and Created are wired as separate
 * headers, so validating against it would reject `?sortBy=status_id` even though
 * clicking the Status header produces exactly that.
 *
 * Duplicated rather than imported because nothing in `@steerfi/shared` exposes the
 * list, and sibling DTOs each define their own locally. Keep the two in step.
 */
const SORTABLE_FIELDS = [
  'provider_name',
  'provider_code',
  'license_number',
  'created_at',
  'updated_at',
  'status_id',
] as const;

type SortableField = (typeof SORTABLE_FIELDS)[number];

/** Narrows rather than casting, so the caller needs no `as`. */
const isSortableField = (value: string | null): value is SortableField =>
  SORTABLE_FIELDS.some((field) => field === value);

/**
 * The label style this page already used, extracted only because `SearchInput`
 * now needs it passed in as `labelClassName` — it renders its own label, and
 * without this the Search label would not match the two beside it.
 *
 * Carries no margin. `SearchInput` wraps its label in `flex flex-col gap-2`, so
 * a `mb-` here would stack on that gap and sit the Search label 8px above the
 * other two. Spacing belongs to the wrapper, which is why the Status and Role
 * wrappers use the same `gap-2` rather than the `mb-1.5` they had before.
 */
const FILTER_LABEL_CLASS = 'text-xs font-medium text-muted-foreground uppercase tracking-wide';

const DEFAULT_PAGE_SIZE = 10;
/** Typed against the allowlist, so changing it to a field the API rejects will not compile. */
const DEFAULT_SORT_BY: SortableField = 'provider_name';
const DEFAULT_ORDER = 'asc';

/**
 * Reads a filter that may appear as repeated params (`?role=4&role=5`) or, from
 * an older or hand-edited link, as a single comma-joined value (`?role=4,5`).
 *
 * Accepting CSV on the way in is not inconsistent with the client only ever
 * emitting repeated params: being liberal about links people paste costs nothing,
 * while the outbound format has to match what the backend parses.
 *
 * Adapted from `suppliers.tsx:52` rather than copied. That version checks
 * `getAll()` first and only falls back to splitting `get()` when `getAll()` is
 * empty — but `getAll()` returns `['4,5']` for a comma-joined param, so its
 * length is 1 and the split branch is unreachable. The CSV support there is dead
 * code, and `Number('4,5')` is `NaN`, so such a link silently loses its filter.
 * Splitting every entry handles repeated, comma-joined, and mixed forms alike.
 */
function getAllOrCsv(searchParams: URLSearchParams, key: string): string[] {
  return searchParams
    .getAll(key)
    .flatMap((v) => v.split(','))
    .map((s) => s.trim())
    .filter(Boolean);
}

/**
 * Serialises filters for the address bar. Defaults are omitted so a pristine
 * view has a clean URL, and arrays are appended per element to match how the API
 * client sends them.
 */
function buildProvidersSearchParams(filters: ProviderListQuery): URLSearchParams {
  const params = new URLSearchParams();
  if (filters.page && filters.page !== 1) params.set('page', String(filters.page));
  if (filters.pageSize && filters.pageSize !== DEFAULT_PAGE_SIZE) {
    params.set('pageSize', String(filters.pageSize));
  }
  if (filters.sortBy && filters.sortBy !== DEFAULT_SORT_BY) params.set('sortBy', filters.sortBy);
  if (filters.order && filters.order !== DEFAULT_ORDER) params.set('order', filters.order);

  const status = filters.status;
  const statusArr = Array.isArray(status) ? status : status ? [status] : [];
  statusArr.filter(Boolean).forEach((v) => params.append('status', v));

  const role = filters.role;
  const roleArr = Array.isArray(role) ? role : role !== undefined ? [role] : [];
  roleArr.forEach((v) => params.append('role', String(v)));

  if (filters.search) params.set('search', filters.search);
  return params;
}

/** Turns a query string into filters, dropping anything the API would reject. */
function filtersFromSearchParams(searchParams: URLSearchParams): ProviderListQuery {
  const statuses = getAllOrCsv(searchParams, 'status').filter(
    (s): s is ProviderStatusFilter => s === 'active' || s === 'inactive'
  );
  const roles = getAllOrCsv(searchParams, 'role')
    .map(Number)
    .filter((n) => ROLE_OPTIONS.some((o) => o.value === String(n)));
  const pageParam = Number(searchParams.get('page'));
  const order = searchParams.get('order');
  const search = searchParams.get('search')?.trim();
  const sortByParam = searchParams.get('sortBy');

  return {
    /**
     * `isSafeInteger`, not `isFinite` or `isInteger`:
     *
     * - `?page=2.5` clears a finite-and-positive check, then fails the backend's
     *   `.int()`, so the page 400s on load.
     * - `?page=1e21` clears `isInteger` too, and the backend caps nothing, so the
     *   offset reaches Postgres as 2e22 and it answers `bigint out of range`. That
     *   is a 500 rather than a graceful fallback, which is worse than the 400.
     */
    page: Number.isSafeInteger(pageParam) && pageParam > 0 ? pageParam : 1,
    pageSize: DEFAULT_PAGE_SIZE,
    sortBy: isSortableField(sortByParam) ? sortByParam : DEFAULT_SORT_BY,
    order: order === 'desc' ? 'desc' : 'asc',
    ...(search ? { search } : {}),
    ...(statuses.length > 0 ? { status: statuses } : {}),
    ...(roles.length > 0 ? { role: roles } : {}),
  };
}

export const Providers: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();

  // Seed from the URL on the first render so state and address bar agree before
  // either sync effect runs. Computed once — later URL changes go through the
  // hydration effect below.
  const [initialFilters] = useState(() => filtersFromSearchParams(searchParams));

  const {
    providers,
    pagination,
    isLoading,
    error,
    filters,
    setSearch,
    setStatus,
    setRole,
    setPage,
    setSort,
    applyFilters,
    refetch,
  } = useProviders(initialFilters);

  const lastWrittenQueryRef = useRef<string | null>(null);
  // Read inside the hydration effect without making `filters` a dependency —
  // depending on it there would re-run hydration on every filter change.
  const filtersRef = useRef(initialFilters);

  // URL -> state, for back/forward and pasted links. Two guards: the ref stops
  // this reacting to our own write, and the equality check stops a redundant
  // state update on mount, which would otherwise refetch immediately.
  useEffect(() => {
    const current = searchParams.toString();
    if (lastWrittenQueryRef.current && lastWrittenQueryRef.current === current) {
      lastWrittenQueryRef.current = null;
      return;
    }
    if (buildProvidersSearchParams(filtersRef.current).toString() === current) return;

    applyFilters(filtersFromSearchParams(searchParams));
  }, [searchParams, applyFilters]);

  // state -> URL, so refresh and sharing preserve the current view.
  useEffect(() => {
    filtersRef.current = filters;
    const nextParams = buildProvidersSearchParams(filters);
    const next = nextParams.toString();
    if (next === searchParams.toString()) return;
    lastWrittenQueryRef.current = next;
    setSearchParams(nextParams, { replace: true });
  }, [filters, searchParams, setSearchParams]);

  const [addOpen, setAddOpen] = useState(false);
  const [editProvider, setEditProvider] = useState<ProviderApiRow | null>(null);
  const [deleteProvider, setDeleteProvider] = useState<ProviderApiRow | null>(null);

  const selectedStatuses = Array.isArray(filters.status)
    ? filters.status
    : filters.status
      ? [filters.status]
      : [];
  const selectedRoles = Array.isArray(filters.role)
    ? filters.role
    : filters.role !== undefined
      ? [filters.role]
      : [];

  const total = pagination?.total ?? 0;
  const page = pagination?.page ?? 1;
  const pageSize = pagination?.pageSize ?? 10;
  const totalPages = pagination?.totalPages ?? 1;
  const startItem = total === 0 ? 0 : (page - 1) * pageSize + 1;
  const endItem = Math.min(page * pageSize, total);

  return (
    <div className="flex flex-col gap-4 py-4 md:gap-6 md:py-6">
      <div className="px-4 lg:px-6">
        <div className="flex items-start justify-between">
          <div>
            <h1 className="text-2xl font-bold tracking-tight">Providers</h1>
            <p className="text-sm text-muted-foreground mt-1">
              Payment-processing partners, banks, payment rails, card networks, and wallets.
            </p>
          </div>
          <Button onClick={() => setAddOpen(true)}>
            <IconPlus size={16} className="mr-1" />
            Add Provider
          </Button>
        </div>
      </div>

      <div className="px-4 lg:px-6">
        <div className="flex items-end gap-3 mb-4">
          <div className="flex flex-col gap-2">
            <Label htmlFor="providers-status-filter" className={FILTER_LABEL_CLASS}>
              Status
            </Label>
            <MultiSelect
              id="providers-status-filter"
              className="w-[160px]"
              value={selectedStatuses}
              onValueChange={(v) => setStatus(v as ProviderStatusFilter[])}
              placeholder="All statuses"
              options={STATUS_OPTIONS}
            />
          </div>
          <div className="flex flex-col gap-2">
            <Label htmlFor="providers-role-filter" className={FILTER_LABEL_CLASS}>
              Role
            </Label>
            <MultiSelect
              id="providers-role-filter"
              className="w-[180px]"
              value={selectedRoles.map(String)}
              onValueChange={(v) => setRole(v.map(Number))}
              placeholder="All roles"
              options={ROLE_OPTIONS}
            />
          </div>
          {/* No wrapping label here: `SearchInput` always renders its own,
              defaulting to "Search". Adding one alongside it rendered two stacked
              labels, so the component's own label is styled to match instead.

              `sessionStorageKey` puts this in the advanced variant — that is what
              gives the recent-searches dropdown and the two-character minimum. No
              `prefixMap` or `onParsedSearch`, so no prefix parsing happens, which
              is the part the ticket excludes. */}
          <SearchInput
            id="providers-search"
            className="w-[260px]"
            value={filters.search ?? ''}
            onChange={setSearch}
            placeholder="Search by name, code, or license…"
            labelClassName={FILTER_LABEL_CLASS}
            hint="Search by name, code, or license"
            sessionStorageKey="providers_recent_searches"
          />
        </div>

        <ProvidersTable
          providers={providers}
          isLoading={isLoading}
          error={error}
          sortBy={filters.sortBy ?? 'provider_name'}
          order={filters.order ?? 'asc'}
          onSort={setSort}
          onEdit={(p) => setEditProvider(p)}
          onDelete={(p) => setDeleteProvider(p)}
        />

        {!isLoading && !error && total > 0 && (
          <div className="flex items-center justify-between mt-3 text-xs text-muted-foreground">
            <span>
              Showing {startItem} to {endItem} of {total} result{total !== 1 ? 's' : ''}
            </span>
            <div className="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                disabled={page <= 1}
                onClick={() => setPage(page - 1)}
              >
                Previous
              </Button>
              <span>
                Page {page} of {totalPages}
              </span>
              <Button
                variant="outline"
                size="sm"
                disabled={page >= totalPages}
                onClick={() => setPage(page + 1)}
              >
                Next
              </Button>
            </div>
          </div>
        )}
      </div>

      <AddProviderModal open={addOpen} onOpenChange={setAddOpen} onSuccess={refetch} />
      <EditProviderModal
        open={editProvider !== null}
        provider={editProvider}
        onOpenChange={(open) => {
          if (!open) setEditProvider(null);
        }}
        onSuccess={refetch}
      />
      <DeleteProviderDialog
        open={deleteProvider !== null}
        provider={deleteProvider}
        onOpenChange={(open) => {
          if (!open) setDeleteProvider(null);
        }}
        onSuccess={refetch}
      />
    </div>
  );
};
